import React, { useMemo, useState } from 'react';
import {
	IconButton,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Box,
	Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getUrlFromPipeline } from '@/utils/constants';

import { ClaimsIcon } from '@/components/icons/ClaimsIcon';
import { paths } from '@/app/routes';
import ArticleIcon from '@mui/icons-material/Article';
import { ChevronRightOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { EndorsementIcon } from '@/components/icons/Endorsement';
import { RenewalIcon } from '@/components/icons/Renewal';
import { useObjectContext } from '@/hooks/useObjectContext';

export const PolicyTransactionMenu = ({ transaction, slotProps = { button: {} } }) => {
	const { button } = slotProps ?? {};
	const { sx: buttonSx } = button ?? {};
	const { clientRow } = useObjectContext();
	const theme = useTheme();

	const deals = useMemo(() => {
		const deals = transaction?.deal;
		if (!deals) return [];
		return Array.isArray(deals) ? deals.filter(Boolean) : [deals];
	}, [transaction?.deal]);

	const deal = deals[0];
	const dealId = deal?.dealId;

	const [anchorEl, setAnchorEl] = useState(null);
	const [subMenuAnchorEl, setSubMenuAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const subOpen = Boolean(subMenuAnchorEl);
	const navigate = useNavigate();

	const handleClose = (menuType) => {
		if (menuType === 'main') {
			setAnchorEl(null);
		} else {
			setSubMenuAnchorEl(null);
		}
	};

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleRenewDeal = () => {
		if (clientRow?.clientId) {
			navigate(paths.createRenewal, {
				state: {
					clientId: clientRow?.clientId,
					policyId: transaction.PolicyId,
					dealFolder: deal?.yearFolder,
				},
			});
		} else {
			console.error('Missing deal or client information');
		}
	};

	const navigateToEndorsement = () => {
		navigate(paths.createEndorsement, {
			state: {
				client: clientRow,
				policies: [{ Id: transaction.PolicyId }],
			},
		});
	};

	const navigateToClaim = () => {
		navigate(paths.createClaim, {
			state: {
				client: clientRow,
				policyId: transaction.PolicyId,
				transaction,
			},
		});
	};

	const handleOpenViewDealMenu = (event) => {
		event.stopPropagation();
		setSubMenuAnchorEl(event.currentTarget);
	};

	const handleSelectDeal = (selectedDeal) => {
		handleClose('main');
		handleClose('submenu');
		if (!selectedDeal?.dealId) return;
		const viewPath = getUrlFromPipeline(selectedDeal.pipeline);
		navigate(`${viewPath}/${selectedDeal.dealId}`);
	};

	const CLOSE_SUB_PROPS = {
		onMouseEnter: () => {
			if (subOpen) {
				handleClose('submenu');
			}
		},
	};

	const dealLink = useMemo(() => {
		if (!deal?.dealId) return '';
		const path = getUrlFromPipeline(deal?.pipeline);
		return `${path}/${dealId}`;
	}, [deal]);

	return (
		<>
			<IconButton
				aria-label='edit'
				size='small'
				onClick={handleClick}
				sx={{
					...(buttonSx ?? {}),
					'&:hover': {
						...(buttonSx?.['&:hover'] ?? {}),
					},
				}}
			>
				<MoreVertIcon />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={() => {
					handleClose('main');
					handleClose('submenu');
				}}
			>
				<MenuItem onClick={navigateToClaim} key={'menu-claim'}>
					<ListItemIcon>
						<ClaimsIcon
							bgColor={theme.palette.primary.main}
							width='22px'
							height='22px'
							sx={{ ml: '4px' }}
						/>
					</ListItemIcon>
					<ListItemText sx={(theme) => ({ color: theme.palette.primary.main })}>
						Claim
					</ListItemText>
				</MenuItem>
				<MenuItem onClick={navigateToEndorsement} key={'menu-endorse'}>
					<ListItemIcon>
						<EndorsementIcon
							bgColor={theme.palette.primary.main}
							width='22px'
							height='22px'
							sx={{ ml: '4px' }}
						/>
					</ListItemIcon>
					<ListItemText sx={(theme) => ({ color: theme.palette.primary.main })}>
						Endorse
					</ListItemText>
				</MenuItem>
				<MenuItem onClick={handleRenewDeal} {...CLOSE_SUB_PROPS}>
					<ListItemIcon>
						<RenewalIcon
							bgColor={theme.palette.primary.main}
							width='22px'
							height='22px'
							sx={{ ml: '4px' }}
						/>
					</ListItemIcon>
					<ListItemText sx={(theme) => ({ color: theme.palette.primary.main })}>
						Renew
					</ListItemText>
				</MenuItem>

				<Tooltip
					arrow
					disableHoverListener={deals.length > 0}
					title={
						deals.length === 0 ? 'Transaction currently not associated to a deal' : ''
					}
				>
					<span>
						<MenuItem
							disabled={deals.length === 0}
							{...(deals.length === 1 ? CLOSE_SUB_PROPS : {})}
							onMouseEnter={(e) => {
								if (deals.length > 1) {
									handleOpenViewDealMenu(e);
								}
								if (subOpen && deals.length === 1) {
									handleClose('submenu');
								}
							}}
							onClick={(e) => {
								if (deals.length === 1) {
									handleClose('main');
									navigate(dealLink);
									return;
								}
								handleOpenViewDealMenu(e);
							}}
						>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<ListItemIcon>
									<ArticleIcon sx={(theme) => ({ color: theme.palette.primary.main })} />
								</ListItemIcon>
								<ListItemText sx={(theme) => ({ color: theme.palette.primary.main })}>
									{deals.length === 1
										? 'View deal'
										: `View deal (${deals.length})`}
								</ListItemText>
								{deals.length > 1 && (
									<ChevronRightOutlined
										sx={(theme) => ({ color: theme.palette.primary.main })}
									/>
								)}
							</Box>
						</MenuItem>
					</span>
				</Tooltip>
			</Menu>
			<Menu
				anchorEl={subMenuAnchorEl}
				open={subOpen}
				onClose={() => handleClose('submenu')}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				{deals.map((dealItem, index) => (
					<MenuItem
						key={`${dealItem.dealId ?? 'deal'}-${index}`}
						onClick={() => handleSelectDeal(dealItem)}
					>
						<ListItemText
							sx={(theme) => ({ color: theme.palette.primary.main })}
							primary={dealItem.dealName ?? dealItem.dealId ?? `Deal ${index + 1}`}
							secondary={dealItem.yearFolder ?? dealItem.pipeline ?? ''}
						/>
					</MenuItem>
				))}
			</Menu>
		</>
	);
};
